<template>
  <v-dialog
    v-model="modalVisible"
    fullscreen
    transition="fade-transition"
  >
    <div
      v-if="currentMedia"
      class="d-flex flex-column fill-height no-gutters"
      style="background: rgba(0, 0, 0, 0.67);"
    >
      <div class="col col-auto pa-0">
        <v-row
          class="grey darken-4"
          align="center"
          no-gutters
        >
          <v-col
            v-if="!editNameMode"
            class="pa-2 pl-10 white--text"
            cols="auto"
          >
            {{ currentMedia.name }}
          </v-col>
          <v-col
            v-else
            cols="auto"
            class="pl-10 py-2"
          >
            <v-text-field
              v-model="editName"
              filled
              dark
              hide-details
              dense
              style="width: 300px"
              @keyup.enter="saveName()"
            />
          </v-col>
          <v-col
            v-if="!editNameMode && !viewOnly"
            class="pa-2"
          >
            <v-btn
              dark
              large
              icon
              tile
              @click="editName = currentMedia.name; editNameMode = true"
            >
              <v-icon small>
                fal fa-pen
              </v-icon>
            </v-btn>
          </v-col>
          <template v-else-if="!viewOnly">
            <v-col
              class="pl-2"
              cols="auto"
            >
              <v-btn
                dark
                large
                icon
                tile
                type="submit"
                @click="saveName()"
              >
                <v-icon small>
                  fal fa-check
                </v-icon>
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
            >
              <v-btn
                dark
                large
                icon
                tile
                @click="editNameMode = false;"
              >
                <v-icon small>
                  fal fa-times
                </v-icon>
              </v-btn>
            </v-col>
          </template>
          <v-spacer />
          <!-- PDF Toolbar Controls -->
          <v-col
            v-if="isPdf && !pdfLoading && !pdfError && pdfPages > 0"
            cols="auto"
            class="d-flex align-center pr-4"
          >
            <v-btn
              icon
              dark
              small
              :disabled="pdfCurrentPage <= 1"
              @click="previousPage"
            >
              <v-icon small>
                fal fa-chevron-left
              </v-icon>
            </v-btn>
            <span
              class="white--text mx-3 text-body-1"
              style="min-width: 100px; text-align: center"
            >
              {{ pdfCurrentPage }} / {{ pdfPages }}
            </span>
            <v-btn
              icon
              dark
              small
              :disabled="pdfCurrentPage >= pdfPages"
              @click="nextPage"
            >
              <v-icon small>
                fal fa-chevron-right
              </v-icon>
            </v-btn>
            <v-divider
              vertical
              dark
              class="mx-3"
            />
            <v-btn
              icon
              dark
              small
              :disabled="pdfZoom <= 0.5"
              @click="zoomOut"
            >
              <v-icon small>
                fal fa-minus
              </v-icon>
            </v-btn>
            <span
              class="white--text mx-3 text-body-1"
              style="min-width: 60px; text-align: center"
            >
              {{ Math.round(pdfZoom * 100) }}%
            </span>
            <v-btn
              icon
              dark
              small
              :disabled="pdfZoom >= 3"
              @click="zoomIn"
            >
              <v-icon small>
                fal fa-plus
              </v-icon>
            </v-btn>
          </v-col>
          <v-col
            v-if="!viewOnly && (currentMedia.tags.length > 0 || tags.data.length > 0)"
            cols="auto"
            class="pr-10"
          >
            <v-combobox
              v-model="currentMedia.tags"
              solo
              dark
              multiple
              background-color="rgba(255, 255, 255, 0.1)"
              hide-details
              :label="$t('individual_analysis.training_exercise.group_card.tags')"
              dense
              chips
              small-chips
              deletable-chips
              :items="tags.data"
              :loading="tags.loading"
              return-object
              item-value="id"
              item-text="label"
              style="width: 500px"
              :delimiters="[',']"
              @blur="updateMediaTags()"
            >
              <template v-slot:selection="data">
                <v-tooltip
                  bottom
                  :disabled="data.item.label && data.item.label.length < 50"
                >
                  <template v-slot:activator="{ on }">
                    <v-chip
                      small
                      v-on="on"
                    >
                      <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                        {{ data.item.label ? data.item.label : data.item }}
                      </span>
                      <v-icon
                        x-small
                        right
                        @click="currentMedia.tags.splice(currentMedia.tags.indexOf(data.item), 1)"
                      >
                        fal fa-times
                      </v-icon>
                    </v-chip>
                  </template>
                  {{ data.item.label ? data.item.label : data.item }}
                </v-tooltip>
              </template>
            </v-combobox>
          </v-col>
          <v-col
            v-if="!viewOnly && !hasNoPlayers"
            cols="auto"
            class="pr-4"
          >
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                cols="auto"
              >
                <v-switch
                  v-model="currentMedia.player_visibility"
                  class="d-inline-block pa-0 ma-0"
                  hide-details
                  inset
                  dense
                  dark
                  :color="!currentMedia.player_visibility ? undefined : 'success'"
                  @change="togglePlayerVisibility()"
                  @click.prevent.stop
                >
                  <template v-slot:label>
                    <span class="font-weight-regular">{{ $t('visible_by_player.info') }}</span>
                  </template>
                </v-switch>
              </v-col>
              <v-col
                v-if="hasShareControl && $can('share-organisation-media')"
                cols="auto"
                class="pl-4"
              >
                <v-switch
                  v-model="currentMedia.is_shared"
                  class="d-inline-block pa-0 ma-0"
                  hide-details
                  inset
                  dense
                  dark
                  :color="!currentMedia.is_shared ? undefined : 'success'"
                  @change="toggleShareAcrossOrganisation()"
                  @click.prevent.stop
                >
                  <template v-slot:label>
                    <span class="font-weight-regular">{{ $t('switch.share_across_organisation') }}</span>
                  </template>
                </v-switch>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            v-if="!viewOnly && canDelete"
            cols="auto"
            class="pa-2"
          >
            <v-btn
              dark
              large
              icon
              tile
              @click="startDelete(currentMedia)"
            >
              <v-icon small>
                fal fa-trash
              </v-icon>
            </v-btn>
          </v-col>
          <v-col
            v-if="(isVideo && canDownloadVideo) || canDownload"
            cols="auto"
            class="pa-2"
          >
            <v-btn
              dark
              large
              icon
              tile
              :href="currentMedia.download_url"
              target="_blank"
            >
              <v-icon small>
                fal fa-download
              </v-icon>
            </v-btn>
          </v-col>
          <v-col
            cols="auto"
            class="pa-2"
          >
            <v-btn
              dark
              large
              icon
              tile
              @click="modalVisible = false"
            >
              <v-icon small>
                fal fa-times
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <div
        class="col pa-0"
        style="min-height: 0"
        @click="modalVisible = false"
      >
        <v-row
          no-gutters
          align="center"
          class="fill-height"
        >
          <v-col
            cols="auto"
            class="pa-6"
          >
            <v-btn
              v-if="hasPreviousMedia"
              icon
              dark
              x-large
              @click.prevent.stop="previousMedia()"
            >
              <v-icon large>
                fal fa-arrow-left
              </v-icon>
            </v-btn>
          </v-col>
          <v-col class="text-center fill-height d-flex align-center justify-center py-3">
            <img
              v-if="!currentMedia.mime_type"
              :src="currentMedia"
              height="600px"
              @click.prevent.stop
              @contextmenu="onContextMenu"
            >
            <img
              v-else-if="currentMedia.mime_type.includes('image/')"
              :src="currentMedia.url"
              style="width: auto; height: auto; max-height: 100%"
              @click.prevent.stop
              @contextmenu="onContextMenu"
            >
            <video
              v-else-if="modalVisible && isVideo"
              controls
              autoplay
              height="600px"
              style="max-height: 100%"
              :src="currentMedia.url"
              @click.prevent.stop
              @contextmenu="onContextMenu"
            />
            <div
              v-else-if="modalVisible && currentMedia.mime_type.includes('pdf')"
              ref="pdfContainer"
              style="width: 100%; height: 1200px; max-height: 100%; max-width: 100%; background: #525659; overflow: auto"
              @click.prevent.stop
              @contextmenu="onContextMenu"
            >
              <canvas
                v-for="page in pdfPages"
                :key="page"
                :ref="`pdfCanvas${page}`"
                style="display: block; margin: 20px auto"
              />
              <div
                v-if="pdfLoading"
                class="white--text text-center pa-4"
              >
                {{ $t('card_title.loading_pdf') }}
              </div>
              <div
                v-if="pdfError"
                class="white--text opacity--05 text-subtitle-1 text-center pa-4"
              >
                {{ $t('card_title.unable_display_pdf') }} <a
                  :href="currentMedia.url"
                  target="_blank"
                >Download</a> instead.
              </div>
            </div>
            <div
              v-else
              class="white--text opacity--05 text-subtitle-1"
              @click.prevent.stop
            >
              {{ $t('card_title.no_preview_available') }}
            </div>
          </v-col>
          <v-col
            cols="auto"
            class="pa-6"
          >
            <v-btn
              v-if="hasNextMedia"
              icon
              dark
              x-large
              @click.prevent.stop="nextMedia()"
            >
              <v-icon large>
                fal fa-arrow-right
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="destroy()"
    />
  </v-dialog>
</template>

<script>

import { MediaApiService } from '@/data/services/MediaApiService'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import { cloneDeep } from 'lodash'
import { TagApiService } from '@/data/services/TagApiService'
import * as pdfjsLib from 'pdfjs-dist'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

export default {
  name: 'MediaViewerDialog',
  components: { DeleteConfirmationDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    player: {
      type: Object,
      default: () => undefined
    },
    media: {
      type: Array,
      default: () => ([])
    },
    viewMediaId: {
      type: Number,
      default: undefined
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    canDownloadVideo: {
      type: Boolean,
      default: true
    },
    canDownload: {
      type: Boolean,
      default: true
    },
    hasShareControl: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentMedia: null,
      editNameMode: false,
      editName: '',
      deleting: false,
      editing: false,
      tags: {
        loading: false,
        data: []
      },
      pdfPages: 0,
      pdfLoading: false,
      pdfError: false,
      pdfCurrentPage: 1,
      pdfZoom: 1.0,
      pdfDocument: null
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        if (!value) this.currentMedia = null
        this.$emit('update:visible', value)
      }
    },
    hasNextMedia() {
      if (!this.currentMedia) return false

      const index = this.media.findIndex(m => m.id === this.currentMedia.id)

      return !!this.media[index + 1]
    },
    hasPreviousMedia() {
      if (!this.currentMedia) return false

      const index = this.media.findIndex(m => m.id === this.currentMedia.id)

      return !!this.media[index - 1]
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    hasNoPlayers() {
      return this.user.organisation_settings?.has_no_players || false
    },
    isVideo() {
      return this.currentMedia && (this.currentMedia.mime_type?.includes('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv'].indexOf(this.currentMedia.extension) > -1)
    },
    isPdf() {
      return this.currentMedia && this.currentMedia.mime_type && this.currentMedia.mime_type.includes('pdf')
    }
  },
  watch: {
    modalVisible(value) {
      if (!value) return

      this.fetchTags()
      this.editNameMode = false

      if (this.viewMediaId) {
        this.currentMedia = cloneDeep(this.media.find(m => m.id === this.viewMediaId))
      } else {
        this.currentMedia = this.media.length > 0 ? cloneDeep(this.media[0]) : undefined
      }
    },
    currentMedia: {
      immediate: true,
      handler(newMedia) {
        if (newMedia && newMedia.mime_type && newMedia.mime_type.includes('pdf')) {
          this.$nextTick(() => {
            this.loadPdf()
          })
        } else {
          this.pdfPages = 0
          this.pdfLoading = false
          this.pdfError = false
        }
      }
    }
  },
  created() {
    const this2 = this

    this.keyListener = (event) => {
      if (event.key === 'ArrowRight') {
        this2.nextMedia()
      } else if (event.key === 'ArrowLeft') {
        this2.previousMedia()
      }
    }

    window.addEventListener('keyup', this.keyListener)
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.keyListener)
  },
  methods: {
    nextMedia() {
      if (!this.currentMedia) return

      this.editNameMode = false

      const index = this.media.findIndex(m => m.id === this.currentMedia.id)

      if (this.media[index + 1]) this.currentMedia = this.media[index + 1]
    },
    previousMedia() {
      if (!this.currentMedia) return

      this.editNameMode = false

      const index = this.media.findIndex(m => m.id === this.currentMedia.id)

      if (this.media[index - 1]) this.currentMedia = this.media[index - 1]
    },
    async saveName() {
      this.editing = true

      try {
        const response = await MediaApiService.update(this.currentMedia.uuid, {
          name: this.editName
        })

        this.currentMedia = response.data
        this.editNameMode = false

        const index = this.media.findIndex(m => m.id === response.data.id)

        if (index > -1) this.media.splice(index, 1, response.data)

        this.$emit('media:updated', response.data)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_title_update.success'),
          color: 'success'
        })
      } catch (e) {

      } finally {
        this.editing = false
      }
    },
    startDelete(media) {
      this.deleteMedia = media
      this.deleteConfirmationDialogVisible = true
    },
    async destroy() {
      if (!this.deleteMedia) return

      const id = this.deleteMedia.id

      try {
        this.deleting = true

        await MediaApiService.delete(id)

        this.modalVisible = false

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.$emit('media:deleted', this.deleteMedia)

        this.deleteConfirmationDialogVisible = false

        this.deleteMedia = undefined
      } catch (e) {

      } finally {
        this.deleting = false
      }
    },
    async togglePlayerVisibility() {
      if (!this.currentMedia) return

      const id = this.currentMedia.id

      try {
        const response = await MediaApiService.togglePlayerVisibility(id)

        const media = response.data

        if (media.player_visibility) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.player_able_to_see_media.success'),
            color: 'success'
          })
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.player_not_able_to_see_media.success'),
            color: 'success'
          })
        }

        this.$emit('media:updated', media)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_able_to_see_media.error'),
          color: 'error'
        })

        throw e
      }
    },
    async fetchTags() {
      this.tags.loading = true

      try {
        const result = await TagApiService.getAll({
          sport_id: this.currentTeam.sport.id
        })
        this.tags.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_tags.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.tags.loading = false
      }
    },
    async toggleShareAcrossOrganisation() {
      if (!this.currentMedia) return

      this.editing = true

      try {
        const response = await MediaApiService.update(this.currentMedia.uuid, {
          is_shared: this.currentMedia.is_shared
        })

        const media = response.data

        this.currentMedia = media

        const index = this.media.findIndex(m => m.id === media.id)

        if (index > -1) this.media.splice(index, 1, media)

        this.$emit('media:updated', media)
      } catch (e) {

      } finally {
        this.editing = false
      }
    },
    async updateMediaTags() {
      this.editing = true

      try {
        const response = await MediaApiService.update(this.currentMedia.uuid, {
          name: this.currentMedia.name,
          tags: this.currentMedia.tags.map(t => {
            if (Array.isArray(t)) return t.id

            return t
          })
        })

        this.currentMedia = response.data
        this.editNameMode = false

        const index = this.media.findIndex(m => m.id === response.data.id)

        if (index > -1) this.media.splice(index, 1, response.data)

        this.$emit('media:updated', response.data)
      } catch (e) {

      } finally {
        this.editing = false
      }
    },
    async loadPdf() {
      if (!this.currentMedia || !this.currentMedia.url) return

      this.pdfLoading = true
      this.pdfError = false
      this.pdfPages = 0
      this.pdfCurrentPage = 1

      try {
        const loadingTask = pdfjsLib.getDocument(this.currentMedia.url)
        const pdf = await loadingTask.promise

        this.pdfDocument = pdf
        this.pdfPages = pdf.numPages

        this.pdfLoading = false

        await this.$nextTick()
        await this.renderAllPages()
      } catch (error) {
        this.pdfError = true
        this.pdfLoading = false
      }
    },
    async renderAllPages() {
      if (!this.pdfDocument) return

      try {
        const container = this.$refs.pdfContainer
        if (!container) return

        for (let pageNum = 1; pageNum <= this.pdfPages; pageNum++) {
          const page = await this.pdfDocument.getPage(pageNum)

          const scale = (96 / 72) * this.pdfZoom

          const viewport = page.getViewport({ scale })

          const canvasRefs = this.$refs[`pdfCanvas${pageNum}`]
          if (!canvasRefs || !canvasRefs.length) continue

          const canvas = canvasRefs[0]
          const context = canvas.getContext('2d')

          canvas.height = viewport.height
          canvas.width = viewport.width

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          }

          await page.render(renderContext).promise
        }
      } catch (error) {

      }
    },
    nextPage() {
      if (this.pdfCurrentPage < this.pdfPages) {
        this.pdfCurrentPage++
        this.scrollToPage(this.pdfCurrentPage)
      }
    },
    previousPage() {
      if (this.pdfCurrentPage > 1) {
        this.pdfCurrentPage--
        this.scrollToPage(this.pdfCurrentPage)
      }
    },
    scrollToPage(pageNum) {
      this.$nextTick(() => {
        const canvasRefs = this.$refs[`pdfCanvas${pageNum}`]
        if (canvasRefs && canvasRefs.length > 0) {
          canvasRefs[0].scrollIntoView({ block: 'start' })
        }
      })
    },
    zoomIn() {
      if (this.pdfZoom < 3) {
        this.pdfZoom += 0.25
        this.renderAllPages()
      }
    },
    zoomOut() {
      if (this.pdfZoom > 0.5) {
        this.pdfZoom -= 0.25
        this.renderAllPages()
      }
    },
    onContextMenu(e) {
      if (this.isVideo && !this.canDownloadVideo) {
        e.preventDefault()
        return
      }

      if (!this.canDownload) {
        e.preventDefault()
        return
      }

      return true
    }
  }
}
</script>

<style scoped>

</style>
